/**
 * Client-side attachment encoding for the public API.
 *
 * Files attach to `/v1/chat/completions` as inline content blocks — the same
 * stateless contract SDK consumers use (see `packages/api/shared/attachments.py`
 * and `tests/api/test_file_attachments.py`):
 *
 *   images    → { type: 'image_url', image_url: { url: 'data:<mime>;base64,…' } }
 *   documents → { type: 'file', file: { filename, file_data: 'data:<mime>;base64,…' } }
 *               (requires plugins: [{ id: 'file-parser' }] on the request)
 *
 * The limits and MIME allowlists here mirror the server's `attachments.py`
 * exactly, so invalid attachments fail fast in the UI instead of as a 4xx.
 */

// --- Limits (mirror attachments.py) ---
export const MAX_FILE_BYTES = 4 * 1024 * 1024; // 4 MB raw per file
export const MAX_TOTAL_FILE_BYTES = 7 * 1024 * 1024; // 7 MB raw per request
export const MAX_FILES_PER_REQUEST = 5;

const IMAGE_MIMES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

const DOC_MIMES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
  'text/markdown',
  'application/json',
  'text/x-python',
  'text/javascript',
  'text/x-c',
  'text/x-go',
  'application/xml',
  'text/xml',
]);

// Browsers report empty or wrong MIME types for many extensions (e.g. '' for
// .md/.py, video/mp2t for .ts), so the extension map is authoritative and the
// browser-reported type is only a fallback. Every text-like source format the
// picker accepts maps to a server-allowlisted MIME (text/plain when there is
// no closer match).
const EXT_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  md: 'text/markdown',
  json: 'application/json',
  py: 'text/x-python',
  js: 'text/javascript',
  c: 'text/x-c',
  h: 'text/x-c',
  go: 'text/x-go',
  xml: 'application/xml',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  txt: 'text/plain',
  tsv: 'text/plain',
  ts: 'text/plain',
  tsx: 'text/plain',
  jsx: 'text/plain',
  java: 'text/plain',
  cpp: 'text/plain',
  rs: 'text/plain',
  rb: 'text/plain',
  php: 'text/plain',
  sh: 'text/plain',
  cs: 'text/plain',
  html: 'text/plain',
  css: 'text/plain',
  log: 'text/plain',
  yaml: 'text/plain',
  yml: 'text/plain',
  ini: 'text/plain',
};

export interface EncodedAttachment {
  name: string;
  size: number;
  mime: string;
  kind: 'image' | 'file';
  dataUri: string;
}

export class AttachmentValidationError extends Error {}

/** Resolve a server-allowlisted MIME type, or null when unsupported. */
export function resolveMime(file: File): string | null {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const mapped = EXT_MIME[ext];
  if (mapped) return mapped;
  const reported = (file.type || '').toLowerCase();
  if (IMAGE_MIMES.has(reported) || DOC_MIMES.has(reported)) return reported;
  return null;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.onload = () => {
      // readAsDataURL yields "data:<browser-mime>;base64,<payload>"; keep only
      // the payload — the header is rebuilt from the resolved MIME.
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

/** Encode one local file into the public-API attachment shape. */
export async function encodeFile(file: File): Promise<EncodedAttachment> {
  if (file.size > MAX_FILE_BYTES) {
    throw new AttachmentValidationError(
      `${file.name} exceeds the ${MAX_FILE_BYTES / (1024 * 1024)} MB per-file limit`,
    );
  }
  const mime = resolveMime(file);
  if (!mime) {
    throw new AttachmentValidationError(`${file.name} has an unsupported file type`);
  }
  const payload = await readAsBase64(file);
  return {
    name: file.name,
    size: file.size,
    mime,
    kind: IMAGE_MIMES.has(mime) ? 'image' : 'file',
    dataUri: `data:${mime};base64,${payload}`,
  };
}

/** Pre-flight check across the whole request (count + total raw bytes). */
export function validateBatch(files: File[]): void {
  if (files.length > MAX_FILES_PER_REQUEST) {
    throw new AttachmentValidationError(
      `At most ${MAX_FILES_PER_REQUEST} attachments per message`,
    );
  }
  const total = files.reduce((sum, f) => sum + f.size, 0);
  if (total > MAX_TOTAL_FILE_BYTES) {
    throw new AttachmentValidationError(
      `Attachments exceed the ${MAX_TOTAL_FILE_BYTES / (1024 * 1024)} MB total per message`,
    );
  }
}

/** Map encoded attachments to public-API content blocks. */
export function toContentBlocks(
  attachments: EncodedAttachment[],
): Array<Record<string, unknown>> {
  return attachments.map((a) =>
    a.kind === 'image'
      ? { type: 'image_url', image_url: { url: a.dataUri } }
      : { type: 'file', file: { filename: a.name, file_data: a.dataUri } },
  );
}
